import { Button } from '@trackman/web-shared-components';
import Paragraph from 'components/TextComps/Paragraph';
import { TranslationStatusChip } from 'pages/Admin/ModulesV2/TranslationStatusChip/TranslationStatusChip';
import { useSendTrackmanParameterCategoryToTranslation } from 'queryHooks/Admin/TrackmanParameterCategories/useSendTrackmanParameterCategoryToTranslation';
import { useResyncTrackmanParameterCategoryTranslation } from 'queryHooks/Admin/TrackmanParameterCategories/useResyncTrackmanParameterCategoryTranslation';
import { TrackmanParameterCategoryDto } from 'types/admin/trackmanParameterCategories';
import { TranslationStatus } from 'types/admin/modulesV2';

type Props = {
  category: TrackmanParameterCategoryDto;
};

const getLanguageLabel = (code: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    const name = displayNames.of(code);
    return name ? `${name} (${code})` : code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

const canResync = (status: TranslationStatus): boolean =>
  status === 'InTranslation' || status === 'Partial' || status === 'Translated';

export const CategoryTranslationProgress = ({ category }: Props) => {
  const sendToTranslation = useSendTrackmanParameterCategoryToTranslation();
  const resync = useResyncTrackmanParameterCategoryTranslation();

  const languages = Object.entries(category.translationProgress?.statusPerLanguage ?? {});
  const status = category.translationProgress?.status;

  return (
    <div className='translations-column'>
      <div className='categories-column-header'>
        <h2>Translations for {category.title}</h2>
        <Button
          variant='contained'
          intent='secondary'
          size='small'
          onClick={() => sendToTranslation.mutate(category.id)}
          disabled={status === 'InTranslation' || sendToTranslation.isPending}
          isLoading={sendToTranslation.isPending}
        >
          Send to translation
        </Button>
      </div>
      <div className='categories-column-body'>
        {languages.length === 0 ? (
          <Paragraph className='no-results'>
            <>No translation activity yet for this category.</>
          </Paragraph>
        ) : (
          <div className='translation-progress'>
            {languages.map(([language, languageStatus]) => (
              <div key={language} className='translation-progress-row'>
                <div className='language'>{getLanguageLabel(language)}</div>
                <div className='status'>
                  <TranslationStatusChip status={languageStatus} />
                </div>
                <div className='actions'>
                  <Button
                    variant='text'
                    intent='secondary'
                    size='small'
                    onClick={() => resync.mutate({ categoryId: category.id, language })}
                    disabled={!canResync(languageStatus) || resync.isPending}
                    isLoading={resync.isPending && resync.variables?.language === language}
                  >
                    Resync
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryTranslationProgress;
